/**
 * K2 report lines (kostnadsslagsindelad resultaträkning and balansräkning)
 * with their BAS account ranges and presentation signs.
 *
 * Conventions shared with src/domain/reports (workstream C):
 *   - ledger balances are debit +, credit −
 *   - a line's amount = `sign` × Σ balances of the accounts it covers
 *     (RR lines and equity/liabilities −1, assets +1), so revenue and equity
 *     show positive and costs negative, as in a printed K2 report
 *   - subtotals/totals sum the ids in `sums`
 *   - accounts are matched against a statement's lines *in order*; the first
 *     line whose ranges contain the account wins (this is how the förening
 *     lines carve Medlemsavgifter out of the generic ranges)
 *   - 8990–8999 (årets resultat) are excluded from the income statement
 *
 * The generic (aktiebolag) ids are mirrored in scripts/import-bas.py as the
 * `k2Line` of every bundled BAS account; src/test/bas.test.ts asserts they
 * agree.
 */
import type { EntityType } from '../types';

export type Statement = 'rr' | 'br';
export type ReportLineKind = 'line' | 'subtotal' | 'total';
export type BalanceSection = 'assets' | 'equity' | 'liabilities';
export type AccountRange = readonly [min: number, max: number];

export interface ReportLineDef {
  /** Stable id such as 'rr.nettoomsattning'; stored on catalog entries. */
  id: string;
  /** Swedish label as printed. */
  label: string;
  statement: Statement;
  kind: ReportLineKind;
  /** Balance sheet only. */
  section?: BalanceSection;
  /** Account ranges (inclusive). Empty for subtotals and totals. */
  ranges: readonly AccountRange[];
  /** Presentation sign: amount = sign × Σ(debit − credit). */
  sign: 1 | -1;
  /** Ids summed by a subtotal/total (lines or earlier subtotals). */
  sums?: readonly string[];
}

/** Accounts that count towards the income statement result. */
export const RR_ACCOUNT_RANGE: AccountRange = [3000, 8989];
/** Never part of the income statement (årets resultat bookings). */
export const RR_EXCLUDED_RANGE: AccountRange = [8990, 8999];

const line = (id: string, label: string, ranges: readonly AccountRange[]): ReportLineDef => ({
  id,
  label,
  statement: 'rr',
  kind: 'line',
  ranges,
  sign: -1,
});

const rrSubtotal = (id: string, label: string, sums: readonly string[], kind: ReportLineKind = 'subtotal'): ReportLineDef => ({
  id,
  label,
  statement: 'rr',
  kind,
  ranges: [],
  sign: -1,
  sums,
});

const br = (
  id: string,
  label: string,
  section: BalanceSection,
  ranges: readonly AccountRange[],
): ReportLineDef => ({
  id,
  label,
  statement: 'br',
  kind: 'line',
  section,
  ranges,
  sign: section === 'assets' ? 1 : -1,
});

const brSubtotal = (
  id: string,
  label: string,
  section: BalanceSection,
  sums: readonly string[],
  kind: ReportLineKind = 'subtotal',
): ReportLineDef => ({
  id,
  label,
  statement: 'br',
  kind,
  section,
  ranges: [],
  sign: section === 'assets' ? 1 : -1,
  sums,
});

// ---- Income statement -------------------------------------------------------

interface RrParts {
  operatingIncome: ReportLineDef[];
  operatingCosts: ReportLineDef[];
  financial: ReportLineDef[];
  appropriations: ReportLineDef[];
  tax: ReportLineDef[];
}

function assembleRr(parts: RrParts): ReportLineDef[] {
  const ids = (defs: ReportLineDef[]) => defs.map((d) => d.id);
  const rorelseresultat = rrSubtotal('rr.rorelseresultat', 'Rörelseresultat', [
    ...ids(parts.operatingIncome),
    ...ids(parts.operatingCosts),
  ]);
  const efterFinansiella = rrSubtotal('rr.resultat_efter_finansiella', 'Resultat efter finansiella poster', [
    rorelseresultat.id,
    ...ids(parts.financial),
  ]);
  const foreSkatt = rrSubtotal('rr.resultat_fore_skatt', 'Resultat före skatt', [
    efterFinansiella.id,
    ...ids(parts.appropriations),
  ]);
  const aretsResultat = rrSubtotal('rr.arets_resultat', 'Årets resultat', [foreSkatt.id, ...ids(parts.tax)], 'total');
  return [
    ...parts.operatingIncome,
    ...parts.operatingCosts,
    rorelseresultat,
    ...parts.financial,
    efterFinansiella,
    ...parts.appropriations,
    foreSkatt,
    ...parts.tax,
    aretsResultat,
  ];
}

const GENERIC_INCOME = (): ReportLineDef[] => [
  line('rr.nettoomsattning', 'Nettoomsättning', [[3000, 3799]]),
  // 3890 Medlemsavgifter (spec supplement) is carved out of 38xx: for an AB/EF
  // it is an övrig rörelseintäkt, never aktiverat arbete.
  line('rr.aktiverat_arbete', 'Aktiverat arbete för egen räkning', [[3800, 3889], [3891, 3899]]),
  line('rr.ovriga_rorelseintakter', 'Övriga rörelseintäkter', [[3890, 3890], [3900, 3999]]),
];

const GENERIC_COSTS = (): ReportLineDef[] => [
  line('rr.ravaror_handelsvaror', 'Råvaror, förnödenheter och handelsvaror', [[4000, 4799]]),
  line('rr.forandring_lager', 'Förändring av lager', [[4800, 4999]]),
  line('rr.ovriga_externa_kostnader', 'Övriga externa kostnader', [[5000, 6999]]),
  line('rr.personalkostnader', 'Personalkostnader', [[7000, 7699]]),
  line('rr.avskrivningar', 'Av- och nedskrivningar av materiella och immateriella anläggningstillgångar', [[7700, 7899]]),
  line('rr.ovriga_rorelsekostnader', 'Övriga rörelsekostnader', [[7900, 7999]]),
];

const GENERIC_FINANCIAL = (): ReportLineDef[] => [
  line('rr.resultat_andelar', 'Resultat från andelar och andra finansiella anläggningstillgångar', [[8000, 8299]]),
  line('rr.ranteintakter', 'Övriga ränteintäkter och liknande resultatposter', [[8300, 8399]]),
  line('rr.rantekostnader', 'Räntekostnader och liknande resultatposter', [[8400, 8499]]),
  line('rr.ovriga_finansiella', 'Övriga finansiella poster', [[8500, 8799]]),
];

const GENERIC_APPROPRIATIONS = (): ReportLineDef[] => [line('rr.bokslutsdispositioner', 'Bokslutsdispositioner', [[8800, 8899]])];
const GENERIC_TAX = (): ReportLineDef[] => [line('rr.skatt', 'Skatt på årets resultat', [[8900, 8989]])];

function genericRr(): ReportLineDef[] {
  return assembleRr({
    operatingIncome: GENERIC_INCOME(),
    operatingCosts: GENERIC_COSTS(),
    financial: GENERIC_FINANCIAL(),
    appropriations: GENERIC_APPROPRIATIONS(),
    tax: GENERIC_TAX(),
  });
}

/**
 * K2 för ideella föreningar (BFNAR 2016:10): Medlemsavgifter, Gåvor, Bidrag
 * and Lämnade bidrag are separate posts; "rörelse" becomes "verksamhet".
 */
function foreningRr(): ReportLineDef[] {
  const income = GENERIC_INCOME().map((d) =>
    d.id === 'rr.ovriga_rorelseintakter' ? { ...d, label: 'Övriga verksamhetsintäkter' } : d,
  );
  const costs = GENERIC_COSTS();
  const externaIndex = costs.findIndex((d) => d.id === 'rr.ovriga_externa_kostnader');
  costs.splice(externaIndex, 0, line('rr.lamnade_bidrag', 'Lämnade bidrag', [[6700, 6719]]));
  const lines = assembleRr({
    operatingIncome: [
      line('rr.medlemsavgifter', 'Medlemsavgifter', [[3890, 3890]]),
      line('rr.gavor', 'Gåvor', [[3993, 3993]]),
      line('rr.bidrag', 'Bidrag', [[3980, 3989]]),
      ...income,
    ],
    operatingCosts: costs,
    financial: GENERIC_FINANCIAL(),
    appropriations: GENERIC_APPROPRIATIONS(),
    tax: GENERIC_TAX(),
  });
  return lines.map((d) => (d.id === 'rr.rorelseresultat' ? { ...d, label: 'Verksamhetsresultat' } : d));
}

/** BRF: årsavgifter (3910/3911, or 3020/3021) and hyror form the Nettoomsättning. */
function brfRr(): ReportLineDef[] {
  const income = GENERIC_INCOME().map((d) => {
    if (d.id === 'rr.nettoomsattning') {
      return { ...d, label: 'Årsavgifter och hyresintäkter', ranges: [[3000, 3799], [3900, 3919]] as const };
    }
    if (d.id === 'rr.ovriga_rorelseintakter') return { ...d, ranges: [[3890, 3890], [3920, 3999]] as const };
    return d;
  });
  return assembleRr({
    operatingIncome: income,
    operatingCosts: GENERIC_COSTS(),
    financial: GENERIC_FINANCIAL(),
    appropriations: GENERIC_APPROPRIATIONS(),
    tax: GENERIC_TAX(),
  });
}

const RR_BY_ENTITY: Readonly<Record<EntityType, readonly ReportLineDef[]>> = {
  aktiebolag: genericRr(),
  enskild_firma: genericRr(),
  ideell_forening: foreningRr(),
  bostadsrattsforening: brfRr(),
};

/** Income statement lines, in presentation order, for an entity type. */
export function incomeStatementLines(entity: EntityType): readonly ReportLineDef[] {
  switch (entity) {
    case 'aktiebolag':
    case 'enskild_firma':
    case 'ideell_forening':
    case 'bostadsrattsforening':
      return RR_BY_ENTITY[entity];
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

/** Ids of the lines that make up the "Förkortad resultaträkning" (Bruttoresultat) for AB. */
export const ABBREVIATED_RR_MERGED: readonly string[] = [
  'rr.nettoomsattning',
  'rr.aktiverat_arbete',
  'rr.ovriga_rorelseintakter',
  'rr.ravaror_handelsvaror',
  'rr.forandring_lager',
];

// ---- Balance sheet ----------------------------------------------------------

const ASSET_LINES = (): ReportLineDef[] => [
  br('br.immateriella', 'Immateriella anläggningstillgångar', 'assets', [[1000, 1099]]),
  br('br.byggnader_mark', 'Byggnader och mark', 'assets', [[1100, 1199]]),
  br('br.maskiner_inventarier', 'Maskiner och inventarier', 'assets', [[1200, 1299]]),
  br('br.finansiella_anlaggningstillgangar', 'Finansiella anläggningstillgångar', 'assets', [[1300, 1399]]),
  brSubtotal('br.summa_anlaggningstillgangar', 'Summa anläggningstillgångar', 'assets', [
    'br.immateriella',
    'br.byggnader_mark',
    'br.maskiner_inventarier',
    'br.finansiella_anlaggningstillgangar',
  ]),
  br('br.varulager', 'Varulager m.m.', 'assets', [[1400, 1499]]),
  br('br.kundfordringar', 'Kundfordringar', 'assets', [[1500, 1599]]),
  br('br.ovriga_fordringar', 'Övriga fordringar', 'assets', [[1600, 1699]]),
  br('br.forutbetalda_kostnader', 'Förutbetalda kostnader och upplupna intäkter', 'assets', [[1700, 1799]]),
  br('br.kortfristiga_placeringar', 'Kortfristiga placeringar', 'assets', [[1800, 1899]]),
  br('br.kassa_bank', 'Kassa och bank', 'assets', [[1900, 1999]]),
  brSubtotal('br.summa_omsattningstillgangar', 'Summa omsättningstillgångar', 'assets', [
    'br.varulager',
    'br.kundfordringar',
    'br.ovriga_fordringar',
    'br.forutbetalda_kostnader',
    'br.kortfristiga_placeringar',
    'br.kassa_bank',
  ]),
  brSubtotal('br.summa_tillgangar', 'Summa tillgångar', 'assets', ['br.summa_anlaggningstillgangar', 'br.summa_omsattningstillgangar'], 'total'),
];

/** Equity sub-posts differ per entity; together they always cover 2000–2099. */
function equityLines(entity: EntityType): ReportLineDef[] {
  switch (entity) {
    case 'aktiebolag':
      return [
        br('br.bundet_eget_kapital', 'Bundet eget kapital', 'equity', [[2000, 2089]]),
        br('br.balanserat_resultat', 'Balanserat resultat', 'equity', [[2090, 2098]]),
        br('br.arets_resultat', 'Årets resultat', 'equity', [[2099, 2099]]),
      ];
    case 'bostadsrattsforening':
      return [
        br('br.bundet_eget_kapital', 'Bundet eget kapital', 'equity', [[2000, 2089]]),
        br('br.balanserat_resultat', 'Balanserat resultat', 'equity', [[2090, 2098]]),
        br('br.arets_resultat', 'Årets resultat', 'equity', [[2099, 2099]]),
      ];
    case 'enskild_firma':
      return [
        br('br.eget_kapital', 'Eget kapital', 'equity', [[2000, 2018], [2020, 2099]]),
        br('br.arets_resultat', 'Årets resultat', 'equity', [[2019, 2019]]),
      ];
    case 'ideell_forening':
      return [
        br('br.eget_kapital', 'Eget kapital', 'equity', [[2000, 2066], [2070, 2099]]),
        br('br.balanserat_resultat', 'Balanserat kapital', 'equity', [[2067, 2068]]),
        br('br.arets_resultat', 'Årets resultat', 'equity', [[2069, 2069]]),
      ];
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

function liabilityLines(): ReportLineDef[] {
  return [
    br('br.obeskattade_reserver', 'Obeskattade reserver', 'liabilities', [[2100, 2199]]),
    br('br.avsattningar', 'Avsättningar', 'liabilities', [[2200, 2299]]),
    br('br.langfristiga_skulder', 'Långfristiga skulder', 'liabilities', [[2300, 2399]]),
    brSubtotal('br.summa_langfristiga_skulder', 'Summa långfristiga skulder', 'liabilities', ['br.langfristiga_skulder']),
    br('br.leverantorsskulder', 'Leverantörsskulder', 'liabilities', [[2440, 2449]]),
    br('br.skatteskulder', 'Skatteskulder', 'liabilities', [[2500, 2599]]),
    br('br.ovriga_kortfristiga_skulder', 'Övriga skulder', 'liabilities', [[2400, 2439], [2450, 2499], [2600, 2899]]),
    br('br.upplupna_kostnader', 'Upplupna kostnader och förutbetalda intäkter', 'liabilities', [[2900, 2999]]),
    brSubtotal('br.summa_kortfristiga_skulder', 'Summa kortfristiga skulder', 'liabilities', [
      'br.leverantorsskulder',
      'br.skatteskulder',
      'br.ovriga_kortfristiga_skulder',
      'br.upplupna_kostnader',
    ]),
  ];
}

function balanceSheetFor(entity: EntityType): ReportLineDef[] {
  const equity = equityLines(entity);
  const summaEgetKapital = brSubtotal(
    'br.summa_eget_kapital',
    'Summa eget kapital',
    'equity',
    equity.map((d) => d.id),
  );
  const total = brSubtotal(
    'br.summa_eget_kapital_skulder',
    'Summa eget kapital och skulder',
    'liabilities',
    ['br.summa_eget_kapital', 'br.obeskattade_reserver', 'br.avsattningar', 'br.summa_langfristiga_skulder', 'br.summa_kortfristiga_skulder'],
    'total',
  );
  return [...ASSET_LINES(), ...equity, summaEgetKapital, ...liabilityLines(), total];
}

const BR_BY_ENTITY: Readonly<Record<EntityType, readonly ReportLineDef[]>> = {
  aktiebolag: balanceSheetFor('aktiebolag'),
  enskild_firma: balanceSheetFor('enskild_firma'),
  ideell_forening: balanceSheetFor('ideell_forening'),
  bostadsrattsforening: balanceSheetFor('bostadsrattsforening'),
};

/** Balance sheet lines (assets, then equity, then liabilities) for an entity type. */
export function balanceSheetLines(entity: EntityType): readonly ReportLineDef[] {
  switch (entity) {
    case 'aktiebolag':
    case 'enskild_firma':
    case 'ideell_forening':
    case 'bostadsrattsforening':
      return BR_BY_ENTITY[entity];
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

// ---- Lookup -----------------------------------------------------------------

export function rangesContain(ranges: readonly AccountRange[], accountNumber: number): boolean {
  return ranges.some(([min, max]) => accountNumber >= min && accountNumber <= max);
}

export function isExcludedFromIncomeStatement(accountNumber: number): boolean {
  return accountNumber >= RR_EXCLUDED_RANGE[0] && accountNumber <= RR_EXCLUDED_RANGE[1];
}

/**
 * The report line an account belongs to for an entity: balance sheet for
 * class 1–2, income statement for 3–8. Undefined for 8990–8999 and numbers
 * outside 1000–8999.
 */
export function reportLineFor(accountNumber: number, entity: EntityType): ReportLineDef | undefined {
  if (accountNumber < 1000 || accountNumber > 8999) return undefined;
  if (accountNumber < 3000) {
    return balanceSheetLines(entity).find((d) => d.kind === 'line' && rangesContain(d.ranges, accountNumber));
  }
  if (isExcludedFromIncomeStatement(accountNumber)) return undefined;
  return incomeStatementLines(entity).find((d) => d.kind === 'line' && rangesContain(d.ranges, accountNumber));
}

/** Convenience: the id of `reportLineFor`. */
export function k2LineIdFor(accountNumber: number, entity: EntityType = 'aktiebolag'): string | undefined {
  return reportLineFor(accountNumber, entity)?.id;
}

/** Every distinct line id across all entity types (for validation and UI). */
export function allReportLineIds(): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const entity of ['aktiebolag', 'enskild_firma', 'ideell_forening', 'bostadsrattsforening'] as const) {
    for (const d of incomeStatementLines(entity)) ids.add(d.id);
    for (const d of balanceSheetLines(entity)) ids.add(d.id);
  }
  return ids;
}
